/* eslint-disable @typescript-eslint/no-explicit-any */

import { InfoCircledIcon } from '@radix-ui/react-icons'
import { Button, Tooltip, Text, Card, Switch } from '@radix-ui/themes'
import { useEffect, useState } from 'react'
import Select, { StylesConfig } from 'react-select'
import {
  deliveryOptions,
  enrollOptions,
  booleanOptions,
  outdatedOptions,
  pushOptions,
  scanOptions,
} from '../../strings/dropdown-options'
import { useAppDispatch } from '../../reducers/store'
import {
  fetchEmailPreferences,
  fetchNotificationPreferences,
  putEmailPreferences,
  putNotificationPreferences,
  selectSettings,
} from '../../reducers/settings'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { EmailPreferences, NotificationPreferences } from '../../types/SettingsType'
import NotificationModal from './NotificationModal'
import { selectUser } from '../../reducers/users'
import chroma from 'chroma-js'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts'
import DropDown from '../dropdown/DropDown'

function NotificationSettings() {
  const di = useAppDispatch()
  const { notificationPreference, emailPreference } = useSelector(selectSettings)
  const [showTemplateModal, setShowTemplateModal] = useState<boolean>(false)
  const { darkModeEnabled } = useSelector(selectUser)

  interface Settings {
    emailFrequency: { value: string; label: string }
    emailAlertFrequency: { value: string; label: string }
    emailOsFrequency: { value: string; label: string }
    scanFrequency: { value: string; label: string }
    pushFrequency: { value: string; label: string }
    deliveryMechanism: { value: string; label: string }
    showThreatDetected: { value: string; label: string }
  }

  const [initialSettings, setInitialSettings] = useState({
    emailFrequency: { value: 'ONCE', label: 'Once' },
    emailAlertFrequency: { value: 'ENABLED', label: 'Enabled' },
    showThreatDetected: { value: 'ENABLED', label: 'Enabled' },
    emailOsFrequency: { value: 'DAILY', label: 'Daily' },
    scanFrequency: { value: 'NEVER', label: 'Never' },
    pushFrequency: { value: 'DAILY', label: 'Daily' },
    deliveryMechanism: { value: 'PUSH', label: 'Push' },
  })

  const [currentSettings, setCurrentSettings] = useState(initialSettings)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      await di(fetchEmailPreferences())
        .then(async () => await di(fetchNotificationPreferences()))
        .finally(() => {
          setIsLoading(false)
        })
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (!isLoading) {
      const enrollMatchingOption =
        emailPreference == undefined
          ? initialSettings.emailFrequency
          : enrollOptions.find((option) => option.value === emailPreference.enrollmentEmailsFrequency)
      const alertMatchingOption =
        emailPreference == undefined
          ? initialSettings.emailAlertFrequency
          : booleanOptions.find((option) => option.value === (emailPreference.threatAlerts ? 'ENABLED' : 'DISABLED'))
      const outdatedMatchingOption =
        emailPreference == undefined
          ? initialSettings.emailOsFrequency
          : outdatedOptions.find((option) => option.value === emailPreference.outdatedOsEmailsFrequency)
      const scanOption =
        notificationPreference == undefined
          ? initialSettings.scanFrequency
          : scanOptions.find((option) => option.value === notificationPreference.deviceIntegrityPeriod)
      const pushFrequencyOption =
        notificationPreference == undefined
          ? initialSettings.pushFrequency
          : pushOptions.find((option) => option.value === notificationPreference.notificationFrequency)
      const deliveryMechanismOption =
        notificationPreference == undefined
          ? initialSettings.deliveryMechanism
          : deliveryOptions.find((option) => option.value === notificationPreference.notificationType)
      const showThreatDetectedOption =
        notificationPreference == undefined
          ? initialSettings.showThreatDetected
          : booleanOptions.find(
              (option) => option.value === (notificationPreference.showThreatDetected ? 'ENABLED' : 'DISABLED')
            )

      const settings = {
        emailFrequency: enrollMatchingOption,
        emailAlertFrequency: alertMatchingOption,
        emailOsFrequency: outdatedMatchingOption,
        scanFrequency: scanOption,
        pushFrequency: pushFrequencyOption,
        deliveryMechanism: deliveryMechanismOption,
        showThreatDetected: showThreatDetectedOption,
      } as Settings

      setCurrentSettings(settings)
      setInitialSettings(settings)
    }
  }, [isLoading])

  const settingsMatch = () => {
    return (
      currentSettings.emailFrequency.value === initialSettings.emailFrequency.value &&
      currentSettings.emailAlertFrequency.value === initialSettings.emailAlertFrequency.value &&
      currentSettings.emailOsFrequency.value === initialSettings.emailOsFrequency.value &&
      currentSettings.scanFrequency.value === initialSettings.scanFrequency.value &&
      currentSettings.pushFrequency.value === initialSettings.pushFrequency.value &&
      currentSettings.deliveryMechanism.value === initialSettings.deliveryMechanism.value &&
      currentSettings.showThreatDetected.value === initialSettings.showThreatDetected.value
    )
  }

  const saveNotificationPreferences = async () => {
    const emailPayload: EmailPreferences = {
      weeklySummary: true,
      threatAlerts: currentSettings.emailAlertFrequency.value === 'ENABLED' ? true : false,
      enrollmentEmailsFrequency: currentSettings.emailFrequency.value,
      outdatedOsEmailsFrequency: currentSettings.emailOsFrequency.value,
    }

    const notificationPayload: NotificationPreferences = {
      notificationType: currentSettings.deliveryMechanism.value,
      deviceIntegrityPeriod: currentSettings.scanFrequency.value,
      notificationFrequency: currentSettings.pushFrequency.value,
      topic: 'OpenAppToScan',
      showThreatDetected: currentSettings.showThreatDetected.value === 'ENABLED' ? true : false,
    }

    const emailsSet = await di(putEmailPreferences(emailPayload))
    const notificationsSet = await di(putNotificationPreferences(notificationPayload))

    if (emailsSet && notificationsSet) {
      toast.success('Notification preferences saved successfully', DEFAULT_TOAST_CONFIG)
      setInitialSettings(currentSettings)
    } else {
      toast.error(
        'Failed to save notification preferences. Please refresh the page and try again.',
        DEFAULT_TOAST_CONFIG
      )
    }
  }

  const combinedStyles: StylesConfig<any, true> = {
    control: (base, state) => ({
      ...base,
      backgroundColor: darkModeEnabled ? '#292828' : '#fff',
      borderColor: darkModeEnabled ? '#555' : '#ccc',
      color: darkModeEnabled ? '#fff' : '#000',
      height: '38px',
      minHeight: '38px',
      boxShadow: state.isFocused ? (darkModeEnabled ? '0 0 0 2px #6958ad' : '0 0 0 2px #aa99ec') : undefined,
      '&:hover': {
        borderColor: state.isFocused ? 'transparent' : '#aa99ec',
        cursor: 'pointer',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      paddingTop: '0px',
      paddingBottom: '0px',
      fontSize: '14px',
    }),
    placeholder: (base) => ({
      ...base,
      color: darkModeEnabled ? '#aaa' : '#555',
      height: '30px',
      minHeight: '30px',
      paddingTop: '0px',
    }),
    singleValue: (base) => ({
      ...base,
      color: darkModeEnabled ? '#fff' : '#000',
    }),
    input: (base) => ({
      ...base,
      height: '36px',
      minHeight: '36px',
      marginTop: '0px',
      marginBottom: '0px',
      paddingTop: '0px',
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkModeEnabled ? '#333' : '#fff',
      color: darkModeEnabled ? '#fff' : '#000',
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected
        ? darkModeEnabled
          ? '#444'
          : '#ddd'
        : isFocused
          ? darkModeEnabled
            ? '#555'
            : '#eee'
          : 'transparent',
      color: darkModeEnabled ? '#fff' : '#000',
    }),
    multiValue: (base, { data }) => ({
      ...base,
      backgroundColor: chroma(data.bgColor).css(),
      marginTop: '-4px',
    }),
    multiValueLabel: (base, { data }) => ({
      ...base,
      color: data.color,
    }),
    multiValueRemove: (base, { data }) => ({
      ...base,
      color: data.color,
      ':hover': {
        backgroundColor: data.color,
        color: 'white',
      },
    }),
  }

  return (
    <>
      <div className="w-full h-[calc(100vh_-_80px)] overflow-auto ">
        <div className="flex pb-1 ">
          <p className="font-semibold text-xl flex-grow flex gap-1 items-center">Notifications</p>

          <div className="flex items-center justify-end">
            <Button
              className="bg-mainPurple hover:bg-tertiaryPurple disabled:hover:bg-mainPurple text-white active:bg-tertiaryPurple disabled:active:bg-mainPurple cursor-pointer disabled:cursor-auto disabled:opacity-50"
              onClick={() => saveNotificationPreferences()}
              disabled={settingsMatch()}
            >
              Save Changes
            </Button>
          </div>
        </div>
        <div className="pb-2 flex flex-col gap-3 mt-2">
          <div className="flex flex-col gap-3">
            <div className="text-lg font-medium">Member Enrollment</div>
            <Card variant="surface" className="w-full flex">
              <div className="grow">
                <p className="text-sm font-semibold">Email New Members</p>
                <p className="text-sm text-textSecondary">
                  Set how often emails should be sent to new members before they enroll their device
                </p>
              </div>
              <Select
                value={currentSettings.emailFrequency}
                defaultValue={enrollOptions.find((option) => option.value === 'ONCE')}
                onChange={(e: any) => setCurrentSettings({ ...currentSettings, emailFrequency: e })}
                options={enrollOptions}
                className={'w-[160px] h-[36px] ml-4 cursor-pointer leading-normal not-form-styled'}
                menuPortalTarget={document.body}
                styles={{ ...combinedStyles }}
              />
            </Card>
            <Card variant="surface" className="w-full flex">
              <div className="grow">
                <p className="text-sm font-semibold">Custom Enrollment Email</p>
                <p className="text-sm text-textSecondary">
                  Edit the enrollment email to suit your organization's needs
                </p>
              </div>
              <div
                className="min-w-40 h-8 px-3 font-medium text-sm items-center rounded hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-900 border border-neutral-7 dark:border-neutral-600 text-paginationSelected cursor-pointer justify-center flex align-middle"
                onClick={() => setShowTemplateModal(true)}
              >
                Edit Email
              </div>
            </Card>
          </div>
          <div className="flex flex-col gap-3">
            <div className="text-lg font-medium">Device Check-ins</div>
            <Card variant="surface" className="w-full flex">
              <div className="grow">
                <p className="text-sm font-semibold">Check-in frequency</p>
                <p className="text-sm text-textSecondary">
                  Notify members when their device hasn’t checked-in for a selected time period
                </p>
              </div>
              <Select
                value={currentSettings.scanFrequency}
                defaultValue={scanOptions.find((option) => option.value === 'NEVER')}
                onChange={(e: any) => setCurrentSettings({ ...currentSettings, scanFrequency: e })}
                options={scanOptions}
                className={'min-w-[160px] h-[36px] ml-4 cursor-pointer not-form-styled'}
                menuPortalTarget={document.body}
                styles={{ ...combinedStyles }}
              />
            </Card>
            <Card variant="surface" className="w-full flex">
              <div className="grow">
                <p className="text-sm font-semibold">Notification Frequency</p>
                <p className="text-sm text-textSecondary">Notify members at a set frequency until checked-in</p>
              </div>
              <DropDown
                value={currentSettings.pushFrequency}
                onChangeHandler={(e: any) => setCurrentSettings({ ...currentSettings, pushFrequency: e })}
                options={pushOptions}
                style={'w-[160px] h-[36px] ml-4 cursor-pointer not-form-styled'}
                disabled={currentSettings.scanFrequency.value === 'NEVER'}
                placeholder=""
                menuPortalTarget={document.body}
                multiple={false}
              />
            </Card>
            <Card variant="surface" className="w-full flex">
              <div className="grow">
                <span className="text-sm font-semibold">Notification Method</span>
                <Tooltip
                  content={
                    <Text>
                      If push is selected and the member has disabled push notifications on their device, email will be
                      used as a fallback.
                    </Text>
                  }
                >
                  <InfoCircledIcon className="inline-block ml-1 mb-0.5 w-4 h-4" />
                </Tooltip>
                <p className="text-sm text-textSecondary">Notify members by push notification or by email</p>
              </div>
              <DropDown
                value={currentSettings.deliveryMechanism}
                onChangeHandler={(e: any) => setCurrentSettings({ ...currentSettings, deliveryMechanism: e })}
                options={deliveryOptions}
                style={'w-[160px] h-[36px] ml-4 cursor-pointer not-form-styled'}
                menuPortalTarget={document.body}
                disabled={currentSettings.scanFrequency.value === 'NEVER'}
                multiple={false}
                placeholder=""
              />
            </Card>
          </div>
          <div className="flex flex-col gap-3">
            <div className="text-lg font-medium">OS Versions</div>
            <Card variant="surface" className="w-full flex">
              <div className="grow">
                <p className="text-sm font-semibold">New iOS Version Available</p>
                <p className="text-sm text-textSecondary">Notify members at a set frequency until they update</p>
              </div>
              <Select
                value={currentSettings.emailOsFrequency}
                defaultValue={outdatedOptions.find((option) => option.value === 'DAILY')}
                onChange={(e: any) => setCurrentSettings({ ...currentSettings, emailOsFrequency: e })}
                options={outdatedOptions}
                className={'min-w-[160px] h-[36px] ml-4 cursor-pointer not-form-styled'}
                menuPortalTarget={document.body}
                styles={{ ...combinedStyles }}
              />
            </Card>
          </div>
          <div className="flex flex-col gap-3">
            <div className="text-lg font-medium">Threat Detections</div>
            <Card variant="surface" className="w-full flex">
              <div className="grow">
                <span className="text-sm font-semibold">
                  Notify Members
                  <Tooltip
                    content={
                      <Text>
                        Sends a push notification to the member. If the member has disabled push notifications on their
                        device, email will be used as a fallback.
                      </Text>
                    }
                  >
                    <InfoCircledIcon className="inline-block ml-1 mb-0.5 w-4 h-4" />
                  </Tooltip>
                </span>
                <p className="text-sm text-textSecondary">
                  Notify members by push notification or by email when a threat has been detected on their device
                </p>
              </div>
              <Switch
                size="2"
                className={`${darkModeEnabled ? 'dark' : 'light'}`}
                checked={currentSettings.showThreatDetected.value === 'ENABLED' ? true : false}
                onCheckedChange={(e: any) =>
                  setCurrentSettings({
                    ...currentSettings,
                    showThreatDetected: e
                      ? { value: 'ENABLED', label: 'Enabled' }
                      : { value: 'DISABLED', label: 'Disabled' },
                  })
                }
                value="on"
              />
            </Card>
            <Card variant="surface" className="w-full flex">
              <div className="grow">
                <p className="text-sm font-semibold">Notify Admins</p>
                <p className="text-sm text-textSecondary">Notify admins by email when a new alert has been opened</p>
              </div>
              <Switch
                size="2"
                className={`${darkModeEnabled ? 'dark' : 'light'}`}
                checked={currentSettings.emailAlertFrequency.value === 'ENABLED' ? true : false}
                onCheckedChange={(e: any) =>
                  setCurrentSettings({
                    ...currentSettings,
                    emailAlertFrequency: e
                      ? { value: 'ENABLED', label: 'Enabled' }
                      : { value: 'DISABLED', label: 'Disabled' },
                  })
                }
                value="on"
              />
            </Card>
          </div>
        </div>
      </div>
      {showTemplateModal && <NotificationModal setShowModal={setShowTemplateModal} />}
    </>
  )
}

export default NotificationSettings
